import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { UserDetails } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { Creator } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/task.pb';
import { Icon, IconName } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { usePopoverDialog, PopoverDialog, Avatar, Text, Button, Dot } from '@frontend/design-system';
import { AIR_RECEPTIONIST_MOCK_ID } from '../../constants';
import { useGetCreatedByList } from '../../hooks/use-get-created-by-list';
import { PersonSearchableListbox } from '../person-searchable-listbox';
import { filtersButtonStyle } from './common-styles';
import { iconLeftSpacing, popoverStyle } from './task-person-filter.styles';

interface TaskPersonFilterProps {
  setValue: (creator: Creator) => void;
  selectedValue: Creator | null;
  label: string;
  iconName?: IconName;
  trackingId: string;
}

export const TaskPersonFilter: FC<TaskPersonFilterProps> = ({
  setValue,
  selectedValue,
  label,
  iconName,
  trackingId,
}) => {
  const createdByList = useGetCreatedByList();
  const { getDialogProps, getTriggerProps, close } = usePopoverDialog();

  const listboxValue = useMemo(() => {
    if (!selectedValue) return '';
    if ('userId' in selectedValue) return selectedValue.userId;
    if ('air' in selectedValue) return AIR_RECEPTIONIST_MOCK_ID;
    return '';
  }, [selectedValue]);

  return (
    <span>
      <Button variant='secondary' css={filtersButtonStyle} {...getTriggerProps()} trackingId={trackingId}>
        <Icon name={iconName ?? 'user'} size={16} />
        <Text weight='regular'>{label}</Text>
        {selectedValue && <Dot color='primary' css={css({ position: 'relative' })} />}
        <Icon name='alt-caret-right-tiny' color='subdued' css={iconLeftSpacing} />
      </Button>
      <PopoverDialog {...getDialogProps()} css={popoverStyle}>
        <PersonSearchableListbox<UserDetails>
          data={createdByList}
          renderItem={(item) => {
            const name = getFullName(item);
            return (
              <>
                <Avatar
                  size='xs'
                  name={name}
                  isUser
                  {...(item.userId === AIR_RECEPTIONIST_MOCK_ID && { isAiAssistant: true, isUser: false })}
                />
                <Text size='large'>{name}</Text>
              </>
            );
          }}
          keyGen={(item) => `searchable-${item.userId}`}
          getItemValue={(item) => item.userId ?? ''}
          value={listboxValue}
          onSelect={(value: string | string[]) => {
            const selectedId = Array.isArray(value) ? value[0] ?? '' : value;
            const val: Creator = selectedId === AIR_RECEPTIONIST_MOCK_ID ? { air: {} } : { userId: selectedId };
            setValue(val);
            close();
          }}
          getSearchValue={(item) => getFullName(item)}
        />
      </PopoverDialog>
    </span>
  );
};
